import type { FC, ReactElement } from 'react';
import React, { useRef, useMemo, Children, cloneElement } from 'react';
import useEvent from 'react-use/lib/useEvent';
import throttle from 'lodash/throttle';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import cn from 'classnames';

import styles from './styles.css';
import { isElementInViewport } from './utils';
import type { TViewAnalytics } from './models';

const SCROLL_THROTTLE_INTERVAL = 50;

/* istanbul ignore next */
const windowThis = typeof window === 'undefined' ? null : window;

const getSendAnalytics = () => {
    let isShown = false;

    return throttle((ref, analytics) => {
        const shouldSendAnalytics = !isShown && isElementInViewport(ref.current);

        /* istanbul ignore next */
        if (shouldSendAnalytics) {
            const payload = {
                event: 'gtm_non_interaction_event',
                path: window.location.pathname,
                action: 'view',
                ...analytics,
            };

            isShown = true;
            sendEvent(payload);
        }
    }, SCROLL_THROTTLE_INTERVAL);
};

export const ViewAnalytics: FC<React.PropsWithChildren<TViewAnalytics>> = ({
    analytics,
    children,
    className,
    passProps = true,
    ...rest
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const sendAnalytics = useMemo(() => getSendAnalytics(), []);

    const handleScroll = () => sendAnalytics(ref, analytics);

    /**
     * useEvent прекращает что-либо делать если target не передали. Мы используем windowThis, чтоб на сср не падало.
     */
    useEvent('scroll', handleScroll, windowThis, { capture: true });
    useEvent('touchmove', handleScroll, windowThis);

    const withProps = Children.map(children as ReactElement | ReactElement[], child =>
        cloneElement(child, rest)
    );

    const classNames = cn(
        {
            [styles.thin]: !children,
        },
        className
    );

    return (
        <div ref={ref} className={classNames}>
            {passProps ? withProps : children}
        </div>
    );
};
